// ============================================================
// Dashboard Store (Zustand)
// ============================================================
// Manages the state of the dashboard, including stats, charts, and activities.
// Caches data to avoid redundant fetches unless schoolId changes.

import { create } from 'zustand';
import dashboardService from '@/services/dashboardService';
import { DATE_RANGES } from '@/components/filters/AnalyticsFilter';

const initialState = {
  stats: null,
  charts: null,
  activities: [],
  
  loading: false,
  error: null,
  
  // Keep track of what we loaded
  loadedSchoolId: null,
  loadedRole: null,
  dateRange: DATE_RANGES.TODAY, // Default date range
};

const useDashboardStore = create((set, get) => ({
  ...initialState,

  setDateRange: (range) => {
    set({ dateRange: range });
    const { loadedSchoolId, loadedRole } = get();
    // Re-fetch data if a school is already loaded
    if (loadedSchoolId && loadedRole) {
      get().fetchDashboardData(loadedSchoolId, loadedRole, true); // true forces refetch
    }
  },

  fetchDashboardData: async (schoolId, role, forceRefetch = false) => {
    const state = get();
    // Prevent refetching if same context and not forced
    if (!forceRefetch && state.loadedSchoolId === schoolId && state.loadedRole === role && state.stats) {
      return;
    }

    set({ loading: true, error: null });

    try {
      const { dateRange } = state;
      const response = await dashboardService.getDashboardData({ schoolId, role, dateRange });
      const { stats, charts, activities } = response.data;

      set({
        stats,
        charts,
        activities,
        loadedSchoolId: schoolId,
        loadedRole: role,
        loading: false,
      });
    } catch (err) {
      set({ 
        error: err.message || 'Failed to load dashboard data',
        loading: false 
      });
    }
  },

  clearDashboardData: () => set(initialState),
}));

export default useDashboardStore;
